import React, { useRef, useState } from 'react';
import { showToast } from '../common/Toaster';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../layout/MainLayout';
import { apiService } from '../../services/api';
import { Camera } from 'lucide-react';
import config from '../../config';

const TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/Mexico_City', label: 'México (CDMX, UTC-6)' },
  { value: 'America/Lima', label: 'Perú / Colombia / Ecuador (UTC-5)' },
  { value: 'America/Bogota', label: 'Colombia (Bogotá, UTC-5)' },
  { value: 'America/Santiago', label: 'Chile (Santiago)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires, UTC-3)' },
  { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo, UTC-3)' },
  { value: 'America/New_York', label: 'EE.UU. Este (Nueva York)' },
  { value: 'Europe/Madrid', label: 'España (Madrid)' },
  { value: 'Asia/Tokyo', label: 'Japón (Tokio, UTC+9)' },
  { value: 'UTC', label: 'UTC' },
];

const ProfilePage: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [tz, setTz] = useState<string>((user as any)?.timezone || 'America/Mexico_City');
  const [tzSaving, setTzSaving] = useState(false);
  const [tzSaved, setTzSaved] = useState(false);

  // Cambio de contraseña (simple: mínimo 6 caracteres)
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 6) { setPwMsg({ type: 'err', text: 'La nueva contraseña debe tener al menos 6 caracteres.' }); return; }
    if (newPw !== confPw) { setPwMsg({ type: 'err', text: 'Las contraseñas no coinciden.' }); return; }
    setPwSaving(true);
    try {
      await changePassword(curPw, newPw);
      setPwMsg({ type: 'ok', text: 'Contraseña actualizada correctamente.' });
      setCurPw(''); setNewPw(''); setConfPw('');
    } catch (err: any) {
      setPwMsg({ type: 'err', text: err?.message || 'No se pudo cambiar la contraseña. Revisa tu contraseña actual.' });
    } finally {
      setPwSaving(false);
    }
  };

  const onTimezone = async (value: string) => {
    setTz(value);
    setTzSaving(true);
    setTzSaved(false);
    try {
      await apiService.put('/auth/profile/timezone', { timezone: value });
      await updateProfile({});
      setTzSaved(true);
      setTimeout(() => setTzSaved(false), 2500);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'No se pudo guardar la zona horaria', 'error');
    } finally {
      setTzSaving(false);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('profile_image', f);
      await apiService.post('/auth/profile/image', fd);
      await updateProfile({});
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'No se pudo subir la imagen', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const getImageUrl = (imagePath?: string | null) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${imagePath}`;
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'super_admin': return 'Super Administrador';
      case 'admin': return 'Administrador';
      case 'mentor': return 'Mentor';
      case 'mentee': return 'Mentee';
      default: return 'Usuario';
    }
  };

  if (!user) return null;

  const imageUrl = getImageUrl(user.profile_image);

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Nombre', value: `${user.first_name} ${user.last_name}` },
    { label: 'Correo', value: user.email },
    { label: 'Rol', value: getRoleLabel(user.role_name) },
    { label: 'Estado', value: user.status === 'active' ? 'Activo' : user.status },
  ];

  return (
    <MainLayout>
      <div className="profile-page" style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.25rem', textAlign: 'left' }}>
        <div className="page-header" style={{ marginBottom: '1.5rem' }}>
          <h1 className="heading-1">Mi Perfil</h1>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.25rem',
            padding: '1.5rem',
            border: '1px solid var(--color-border-light, #e3e1db)',
            borderRadius: 12,
            background: 'var(--color-bg-secondary, #f7f6f3)',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              style={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#e7e1d3',
                color: '#0F1B2D',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.75rem',
                fontWeight: 700,
                opacity: uploading ? 0.5 : 1,
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={`${user.first_name} ${user.last_name}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                getInitials(user.first_name, user.last_name)
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title="Cambiar foto"
              style={{
                position: 'absolute', right: -2, bottom: -2,
                width: 30, height: 30, borderRadius: '50%',
                background: '#101010', color: '#fff', border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: uploading ? 'default' : 'pointer',
              }}
            >
              <Camera size={15} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={onFile}
              style={{ display: 'none' }}
            />
          </div>
          <div>
            <div style={{ fontSize: '1.375rem', fontWeight: 600 }}>
              {user.first_name} {user.last_name}
            </div>
            <div style={{ color: 'var(--color-text-secondary, #6b6f76)' }}>
              {getRoleLabel(user.role_name)}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              style={{
                marginTop: 8, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 600,
                borderRadius: 8, border: '1px solid #d9d7d1', background: '#fff', color: '#101010',
                cursor: uploading ? 'default' : 'pointer',
              }}
            >
              {uploading ? 'Subiendo…' : 'Cambiar foto'}
            </button>
          </div>
        </div>

        <div
          style={{
            border: '1px solid var(--color-border-light, #e3e1db)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {rows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: '1rem',
                alignItems: 'baseline',
                padding: '0.9rem 1.25rem',
                borderTop: i === 0 ? 'none' : '1px solid var(--color-border-light, #e3e1db)',
              }}
            >
              <span style={{ color: 'var(--color-text-secondary, #6b6f76)', textAlign: 'left' }}>{row.label}</span>
              <span style={{ fontWeight: 500, textAlign: 'left' }}>{row.value}</span>
            </div>
          ))}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              gap: '1rem',
              alignItems: 'center',
              padding: '0.9rem 1.25rem',
              borderTop: '1px solid var(--color-border-light, #e3e1db)',
            }}
          >
            <span style={{ color: 'var(--color-text-secondary, #6b6f76)', textAlign: 'left' }}>Zona horaria</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <select
                value={tz}
                disabled={tzSaving}
                onChange={e => onTimezone(e.target.value)}
                style={{
                  padding: '8px 10px', borderRadius: 8, border: '1px solid #d9d7d1',
                  background: '#fff', color: '#101010', fontSize: '0.9rem', fontFamily: 'inherit',
                  minWidth: 260, cursor: tzSaving ? 'default' : 'pointer',
                }}
              >
                {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {tzSaving && <span style={{ fontSize: '0.8rem', color: '#6b6f76' }}>Guardando…</span>}
              {tzSaved && <span style={{ fontSize: '0.8rem', color: '#1F6F4A' }}>✓ Guardada</span>}
            </span>
          </div>
        </div>
        <p style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--color-text-secondary, #6b6f76)' }}>
          Tu zona horaria se usa para crear correctamente las reuniones de Teams de tus sesiones.
        </p>
      </div>

      {/* Cambiar contraseña */}
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto 1.5rem',
          padding: '1.5rem',
          border: '1px solid var(--color-border-light, #e3e1db)',
          borderRadius: 12,
          background: 'var(--color-bg-secondary, #f7f6f3)',
          marginBottom: '1.5rem',
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', color: '#101010' }}>Cambiar contraseña</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--color-text-secondary, #6b6f76)' }}>
          Mínimo 6 caracteres.
        </p>
        <form onSubmit={onChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 380 }}>
          {(() => {
            const inputStyle: React.CSSProperties = {
              padding: '10px 12px', borderRadius: 8, border: '1px solid #d9d7d1',
              background: '#fff', color: '#101010', fontSize: '0.9rem', fontFamily: 'inherit', width: '100%',
            };
            return (
              <>
                <input type="password" autoComplete="current-password" placeholder="Contraseña actual"
                  value={curPw} onChange={e => setCurPw(e.target.value)} style={inputStyle} required />
                <input type="password" autoComplete="new-password" placeholder="Nueva contraseña"
                  value={newPw} onChange={e => setNewPw(e.target.value)} style={inputStyle} required minLength={6} />
                <input type="password" autoComplete="new-password" placeholder="Confirmar nueva contraseña"
                  value={confPw} onChange={e => setConfPw(e.target.value)} style={inputStyle} required minLength={6} />
              </>
            );
          })()}
          {pwMsg && (
            <span style={{ fontSize: '0.85rem', color: pwMsg.type === 'ok' ? '#1F6F4A' : '#C8102E' }}>{pwMsg.text}</span>
          )}
          <div>
            <button type="submit" className="btn btn--primary" disabled={pwSaving}
              style={{ padding: '9px 18px', borderRadius: 8, background: '#0F1B2D', color: '#fff', border: 'none', fontWeight: 600, cursor: pwSaving ? 'default' : 'pointer' }}>
              {pwSaving ? 'Guardando…' : 'Actualizar contraseña'}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default ProfilePage;
